import { Input, Label, Field, Radio, RadioGroup } from '@headlessui/react';
import { faCheckCircle, faChevronUp, faMinusCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import tw from 'twin.macro';
import { IProtectionPlan, ProtectionPlanEnum } from '../../../__generated__/graphql';
import SwitchComponent from '../../components/switch';
import { useMutation, useQuery } from '@apollo/client';
import {
  CREATE_PROTECTION_PLAN,
  GET_PROTECTION_PLANS,
  UPDATE_PROTECTION_PLAN,
} from '../../services/protectionPlanService/queries';
import {
  ICreateProtectionPlanMutation,
  IUpdateProtectionPlanMutation,
  IGetProtectionPlansQuery,
  IUpdateProtectionPlanMutationVariables,
  ICreateProtectionPlanMutationVariables,
} from '../../services/protectionPlanService/__generated__/queries.generated';
import { toast } from 'react-toastify';
import { ProtectionPlanPageSkeleton } from '../../components/skeletons';
import NumericInput from '../../components/NumericInput';
import {
  DECLINED_LIABILITY_PERCENT_DEFAULT,
  DECLINED_LIABILITY_PERCENT_OPTIONS,
  formatDeclinedLiabilityPercent,
  resolveDeclinedProtectionOutOfPocketPercent,
  snapDeclinedLiabilityPercentToAllowedOption,
} from '../../utils/declinedProtectionLiability';

const PageContainer = styled.div`
  ${tw`w-full max-w-full min-w-0 box-border px-4 mb-32`}
`;

const ContentContainer = styled.div`
  ${tw`mx-auto w-full max-w-screen-xl max-w-full min-w-0 box-border`}
`;

const StyledRadioGroup = styled(RadioGroup)`
  ${tw`space-y-2`}
`;

const StyledRadio = styled(Radio)<{ $warning?: boolean }>`
  ${tw`relative flex cursor-pointer rounded-lg py-4 px-5 text-white shadow-card transition`}
  ${({ $warning }) => ($warning ? tw`bg-amber-100` : tw`bg-bg-secondary`)}
  &:focus {
    ${tw`outline-1 outline-red-500`}
  }
  &[data-checked] {
    ${({ $warning }) => ($warning ? tw`bg-amber-200/80` : tw`bg-black/10`)}
  }
`;

const MonthlyAllInactiveWarning = styled.p`
  ${tw`text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-2`}
`;

const RadioContent = styled.div`
  ${tw`flex w-full items-center justify-between`}
`;

const PlanInfo = styled.div`
  ${tw`text-sm/6`}
`;

const PlanName = styled.p`
  ${tw`font-semibold text-text-primary`}
`;

const PlanDetails = styled.div`
  ${tw`flex gap-2 text-text-primary`}
`;

const Separator = styled.div`
  &::before {
    content: '·';
  }
`;

const StyledLabel = styled(Label)`
  ${tw`text-sm/6 font-medium text-text-primary`}
`;

const StyledInput = styled.input`
  ${tw`mt-3 w-full rounded-lg border-none bg-bg-secondary text-sm/6 text-text-primary h-[44px]`}
  &:focus {
    ${tw`outline-none`}
  }
  &[data-focus] {
    ${tw`outline-2 -outline-offset-2 outline-white/25`}
  }
`;

const StyledArea = styled.textarea`
  ${tw`max-w-full h-[300px]`}
`;

const StyledDescriptionArea = styled.textarea`
  ${tw`max-w-full h-[100px]`}
`;

const InputField = styled(Field)<{ inVertical?: boolean }>`
  ${tw`max-w-full bg-black/10 p-4 flex flex-col`}
  ${({ inVertical = true }) => !inVertical && tw`flex flex-row justify-between`}
`;

const InputContainer = styled.div`
  ${tw`max-w-full mt-4 bg-bg-secondary`}
`;

const DeclinedPlanContainer = styled.div`
  ${tw`mb-4`}
`;

const DeclinedDefaultText = styled.div`
  ${tw`text-sm text-text-secondary`}
`;

const StyledSelect = styled.select`
  ${tw`mt-3 w-full rounded-lg border-none bg-bg-secondary text-sm/6 text-text-primary h-[44px] px-3`}
`;

const DeSelectButton = styled.button`
  ${tw`w-full h-[30px] border-none bg-black/10`}
  &:hover {
    ${tw`bg-black/20 cursor-pointer`}
  }
`;

const StyledTitle = styled.h1`
  ${tw`text-3xl font-bold text-text-primary mt-4 mb-10`}
`;

const TabBar = styled.div`
  ${tw`flex bg-bg-secondary rounded-lg p-1 mb-6 max-w-sm`}
`;

const TabButton = styled.button<{ active: boolean }>`
  ${tw`flex-1 py-2 rounded-md font-medium transition-colors border-none cursor-pointer`}
  ${({ active }) =>
    active ? tw`bg-white text-text-primary shadow-xs` : tw`bg-transparent text-text-secondary`}
`;

const defaultDailyPlans = [
  {
    plan: ProtectionPlanEnum.Minimum,
    dailyPrice: 12,
    updatedAt: new Date(),
    policyDetails: 'Medium policy details',
  },
  {
    plan: ProtectionPlanEnum.Standard,
    dailyPrice: 22,
    updatedAt: new Date(),
    policyDetails: 'Standard policy details',
  },
  {
    plan: ProtectionPlanEnum.Pro,
    dailyPrice: 16,
    updatedAt: new Date(),
    policyDetails: 'Pro policy details',
  },
  {
    plan: ProtectionPlanEnum.Premium,
    dailyPrice: 20,
    updatedAt: new Date(),
    policyDetails: 'Premium policy details',
  },
];

const defaultDeclinedPlan = {
  plan: ProtectionPlanEnum.Declined,
  dailyPrice: 0,
  outOfPocket: 0,
  isActive: true,
  isDailyProtectionPlanDeclinable: true,
  isMonthlyProtectionPlanDeclinable: true,
  declinedProtectionOutOfPocketPercent: 100,
  updatedAt: new Date(),
  policyDetails: 'Decline coverage policy details',
  description:
    'You will be responsible for physical damage or theft. Only the state minimum liability protection plan applies.',
};

function mergeDeclinedPlan(apiPlans: IProtectionPlan[] | undefined): IProtectionPlan {
  const match = apiPlans?.find(p => p.plan === ProtectionPlanEnum.Declined);
  if (!match) {
    return defaultDeclinedPlan as unknown as IProtectionPlan;
  }
  return {
    ...(defaultDeclinedPlan as unknown as IProtectionPlan),
    ...match,
    dailyPrice: 0,
  };
}

function paidPlansOnly(apiPlans: IProtectionPlan[] | undefined): IProtectionPlan[] {
  return (apiPlans ?? []).filter(p => p.plan !== ProtectionPlanEnum.Declined);
}

function declinedOutOfPocketLabel(plan: IProtectionPlan, override?: number | null): string {
  const percent = resolveDeclinedProtectionOutOfPocketPercent({
    percent: override ?? (plan as any).declinedProtectionOutOfPocketPercent,
    legacyPolicy: (plan as any).declinedProtectionOutOfPocketAmt,
  });
  return formatDeclinedLiabilityPercent(percent);
}

// For monthly plans, `dailyPrice` stores the flat monthly plan price
// (column name retained for backward compatibility per the spec).
const defaultMonthlyPlans = [
  {
    plan: ProtectionPlanEnum.MonthlyMinimum,
    dailyPrice: 200,
    outOfPocket: 3000,
    isActive: false,
    updatedAt: new Date(),
    policyDetails: 'Monthly minimum coverage',
    description:
      '$3000 max out-of-pocket for vehicle damage or theft. State minimum liability protection.',
  },
  {
    plan: ProtectionPlanEnum.MonthlyStandard,
    dailyPrice: 300,
    outOfPocket: 500,
    isActive: false,
    updatedAt: new Date(),
    policyDetails: 'Monthly standard coverage',
    description:
      '$500 max out-of-pocket for vehicle damage or theft. State minimum liability protection.',
  },
  {
    plan: ProtectionPlanEnum.MonthlyPro,
    dailyPrice: 600,
    outOfPocket: 0,
    isActive: false,
    updatedAt: new Date(),
    policyDetails: 'Monthly pro coverage',
    description: '',
  },
  {
    plan: ProtectionPlanEnum.MonthlyPremium,
    dailyPrice: 800,
    outOfPocket: 0,
    isActive: false,
    updatedAt: new Date(),
    policyDetails: 'Monthly premium coverage',
    description: '',
  },
];

const StyledButton = styled.button`
  ${tw`gap-10 w-full cursor-pointer rounded-lg bg-black px-4 py-3 text-center text-sm font-semibold text-white shadow-xs transition duration-200 ease-in-out border-none`}
  &:hover {
    ${tw`bg-green-700`}
  }
`;

const MONTHLY_PROTECTION_DISPLAY_ORDER: ProtectionPlanEnum[] = [
  ProtectionPlanEnum.MonthlyPremium,
  ProtectionPlanEnum.MonthlyPro,
  ProtectionPlanEnum.MonthlyStandard,
  ProtectionPlanEnum.MonthlyMinimum,
];

const MONTHLY_ALL_INACTIVE_WARNING =
  'Monthly rental will use the daily protection plan × days for the rental, which could be very high and make monthly rental very uncompetitive. Please turn on any one of these monthly protection plans.';

function sortMonthlyPlansForDisplay(plans: IProtectionPlan[]): IProtectionPlan[] {
  return [...plans].sort((a, b) => {
    const ai = MONTHLY_PROTECTION_DISPLAY_ORDER.indexOf(a.plan as ProtectionPlanEnum);
    const bi = MONTHLY_PROTECTION_DISPLAY_ORDER.indexOf(b.plan as ProtectionPlanEnum);
    return (ai < 0 ? 999 : ai) - (bi < 0 ? 999 : bi);
  });
}

function allMonthlyProtectionPlansInactive(plans: IProtectionPlan[]): boolean {
  return plans.length > 0 && plans.every(p => !p.isActive);
}

export default function ProtectionPlanPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selected, setSelected] = useState<IProtectionPlan | null>(null);
  const [mode, setMode] = useState<'daily' | 'monthly'>('daily');
  const radioGroupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const tab = searchParams.get('tab') ?? searchParams.get('mode');
    if (tab === 'monthly') {
      setMode('monthly');
    } else if (tab === 'daily') {
      setMode('daily');
    }
  }, [searchParams]);

  const { data, loading, error, refetch } =
    useQuery<IGetProtectionPlansQuery>(GET_PROTECTION_PLANS);

  // Merge database data with default plans to ensure all 4 plan types are shown
  // For each plan type, use database data if available, otherwise use default
  const apiPaidPlans = paidPlansOnly(data?.protectionPlans as IProtectionPlan[] | undefined);
  const declinedPlan = mergeDeclinedPlan(data?.protectionPlans as IProtectionPlan[] | undefined);
  const defaults = mode === 'monthly' ? defaultMonthlyPlans : defaultDailyPlans;
  const mergedPlans = defaults.map(defaultPlan => {
    const dbPlan = apiPaidPlans.find(p => p.plan === defaultPlan.plan);
    return dbPlan ? (dbPlan as IProtectionPlan) : (defaultPlan as unknown as IProtectionPlan);
  });
  const plans = mode === 'monthly' ? sortMonthlyPlansForDisplay(mergedPlans) : mergedPlans;
  const showAllMonthlyInactiveWarning =
    mode === 'monthly' && allMonthlyProtectionPlansInactive(plans);

  // Update selected plan when plans data changes (after refetch)
  useEffect(() => {
    if (selected && data?.protectionPlans) {
      const updatedPlan = data.protectionPlans.find(
        p => p.id === selected.id || p.plan === selected.plan,
      );
      if (updatedPlan) {
        setSelected(updatedPlan as IProtectionPlan);
      }
    }
  }, [data?.protectionPlans, selected]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        radioGroupRef.current &&
        !radioGroupRef.current.contains(event.target as Node) &&
        selected !== null
      ) {
        setSelected(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selected]);

  if (loading) return <ProtectionPlanPageSkeleton />;
  if (error) return <div>Error: {error.message}</div>;

  return (
    <PageContainer>
      <ContentContainer>
        <StyledTitle>Protection Plan Set up</StyledTitle>
        <TabBar>
          <TabButton
            type="button"
            active={mode === 'daily'}
            onClick={() => {
              setSelected(null);
              setMode('daily');
              setSearchParams({ tab: 'daily' }, { replace: true });
            }}
          >
            Daily
          </TabButton>
          <TabButton
            type="button"
            active={mode === 'monthly'}
            onClick={() => {
              setSelected(null);
              setMode('monthly');
              setSearchParams({ tab: 'monthly' }, { replace: true });
            }}
          >
            Monthly
          </TabButton>
        </TabBar>
        <DeclinedCoveragePlan
          plan={declinedPlan}
          isSelected={selected?.plan === ProtectionPlanEnum.Declined}
          onDeselect={() => setSelected(null)}
          setSelected={setSelected}
          refetch={refetch}
        />
        <StyledRadioGroup ref={radioGroupRef} value={selected} aria-label="Server size" as="div">
          {showAllMonthlyInactiveWarning ? (
            <MonthlyAllInactiveWarning>{MONTHLY_ALL_INACTIVE_WARNING}</MonthlyAllInactiveWarning>
          ) : null}
          {plans.map(plan => (
            <div key={plan.plan}>
              <EachPlan
                plan={plan as IProtectionPlan}
                isSelected={selected?.plan === plan.plan}
                onDeselect={() => setSelected(null)}
                setSelected={setSelected}
                refetch={refetch}
                useWarningStyle={showAllMonthlyInactiveWarning}
              />
            </div>
          ))}
        </StyledRadioGroup>
      </ContentContainer>
    </PageContainer>
  );
}

export function EachPlan({
  plan,
  isSelected,
  onDeselect,
  setSelected,
  refetch,
  useWarningStyle = false,
}: {
  plan: IProtectionPlan;
  isSelected: boolean;
  onDeselect: () => void;
  setSelected: (plan: IProtectionPlan) => void;
  refetch: () => Promise<any>;
  useWarningStyle?: boolean;
}) {
  const [isActive, setIsActive] = useState(!!plan.isActive);
  const [planDetails, setPlanDetails] = useState(plan.policyDetails || '');
  const [dailyPrice, setDailyPrice] = useState(plan.dailyPrice || 0);
  const [outOfPocket, setOutOfPocket] = useState((plan as any).outOfPocket || 0);
  const [description, setDescription] = useState(plan.description || '');
  const [currentPlan, setCurrentPlan] = useState<IProtectionPlan>(plan);

  useEffect(() => {
    setCurrentPlan(plan);
    setIsActive(!!plan.isActive);
    setDailyPrice(plan.dailyPrice || 0);
    setOutOfPocket((plan as any).outOfPocket || 0);
    setPlanDetails(plan.policyDetails || '');
    setDescription(plan.description || '');
  }, [plan]);

  const [createProtectionPlan, { loading: isCreating }] = useMutation<
    ICreateProtectionPlanMutation,
    ICreateProtectionPlanMutationVariables
  >(CREATE_PROTECTION_PLAN, {
    refetchQueries: [{ query: GET_PROTECTION_PLANS }],
    awaitRefetchQueries: true,
    onCompleted: async () => {
      toast.success('Protection plan created successfully');
      // Ensure the query is refetched to update the UI
      await refetch();
    },
    onError: () => {
      toast.error('Failed to create protection plan');
    },
  });

  const [updateProtectionPlan, { loading: isUpdating }] = useMutation<
    IUpdateProtectionPlanMutation,
    IUpdateProtectionPlanMutationVariables
  >(UPDATE_PROTECTION_PLAN, {
    refetchQueries: [{ query: GET_PROTECTION_PLANS }],
    awaitRefetchQueries: true,
    onCompleted: async data => {
      toast.success('Protection plan updated successfully');
      // Ensure the query is refetched to update the UI
      await refetch();
      // Update current plan state if it was the one being updated
      if (data.updateProtectionPlan && currentPlan.id === data.updateProtectionPlan.id) {
        setCurrentPlan(data.updateProtectionPlan as IProtectionPlan);
        setSelected(data.updateProtectionPlan as IProtectionPlan);
      }
    },
    onError: () => {
      toast.error('Failed to update protection plan');
    },
  });

  const handleSave = () => {
    const input = {
      plan: plan.plan as ProtectionPlanEnum,
      dailyPrice: dailyPrice,
      outOfPocket: outOfPocket,
      description: description,
      isActive: isActive,
      policyDetails: planDetails,
    };
    if (!plan.id) {
      createProtectionPlan({
        variables: {
          input,
        },
      });
    } else {
      updateProtectionPlan({
        variables: {
          input: {
            id: plan.id,
            ...input,
          },
        },
      });
    }
  };

  return (
    <>
      <StyledRadio
        key={plan.plan}
        value={plan}
        $warning={useWarningStyle}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          setSelected(plan);
        }}
        as="div"
      >
        <RadioContent as="div">
          <PlanInfo>
            <PlanName>{plan.plan}</PlanName>
            <PlanDetails>
              <div>
                ${plan.dailyPrice}/{plan.plan?.startsWith('MONTHLY_') ? 'month' : 'day'}
              </div>
              <Separator aria-hidden="true" />
              <div>
                updated on:{' '}
                {plan.updatedAt
                  ? new Date(plan.updatedAt).toLocaleDateString()
                  : new Date(plan.createdAt).toLocaleDateString()}
              </div>
              <Separator aria-hidden="true" />
              <div>{plan.isActive ? 'Active' : 'Inactive'}</div>
            </PlanDetails>
          </PlanInfo>
          {plan.isActive ? (
            <FontAwesomeIcon icon={faCheckCircle} color="green" />
          ) : (
            <FontAwesomeIcon icon={faMinusCircle} color="red" />
          )}
        </RadioContent>
      </StyledRadio>
      {isSelected ? (
        <InputContainer>
          <DeSelectButton onClick={onDeselect}>
            <FontAwesomeIcon icon={faChevronUp} size="xl" color="darkgreen" />
          </DeSelectButton>
          <InputField inVertical={false}>
            <StyledLabel>Active</StyledLabel>
            <SwitchComponent
              isActive={!!isActive}
              onChange={() => setIsActive(!isActive)}
              color="green"
            />
          </InputField>
          <InputField>
            <StyledLabel>
              {plan.plan?.startsWith('MONTHLY_') ? 'Plan price / month' : 'Daily plan price'}
            </StyledLabel>
            <NumericInput
              value={dailyPrice}
              onValueChange={(value: number | null) => setDailyPrice(value ?? 0)}
              width="100%"
            />
          </InputField>
          <InputField>
            <StyledLabel>Out of Pocket Maximum</StyledLabel>
            <NumericInput
              value={outOfPocket}
              onValueChange={(value: number | null) => setOutOfPocket(value ?? 0)}
              width="100%"
            />
          </InputField>
          <InputField>
            <StyledLabel>Description</StyledLabel>
            <StyledDescriptionArea
              value={description}
              onChange={e => {
                e.stopPropagation();
                setDescription(e.target.value);
              }}
            />
          </InputField>
          <InputField>
            <StyledLabel>Plan Details</StyledLabel>
            <StyledArea
              value={planDetails}
              onChange={e => {
                e.stopPropagation();
                setPlanDetails(e.target.value);
              }}
            />
          </InputField>

          <StyledButton onClick={handleSave} disabled={isCreating || isUpdating}>
            Save
          </StyledButton>
        </InputContainer>
      ) : null}
    </>
  );
}

export function DeclinedCoveragePlan({
  plan,
  isSelected,
  onDeselect,
  setSelected,
  refetch,
}: {
  plan: IProtectionPlan;
  isSelected: boolean;
  onDeselect: () => void;
  setSelected: (plan: IProtectionPlan) => void;
  refetch: () => Promise<any>;
}) {
  const [isActive, setIsActive] = useState(!!plan.isActive);
  const [planDetails, setPlanDetails] = useState(plan.policyDetails || '');
  const [liabilityPercent, setLiabilityPercent] = useState<number>(() =>
    snapDeclinedLiabilityPercentToAllowedOption(
      resolveDeclinedProtectionOutOfPocketPercent({
        percent: (plan as any).declinedProtectionOutOfPocketPercent,
        legacyPolicy: (plan as any).declinedProtectionOutOfPocketAmt,
      }),
    ),
  );
  const [description, setDescription] = useState(plan.description || '');
  const [dailyDeclinable, setDailyDeclinable] = useState(
    (plan as any).isDailyProtectionPlanDeclinable !== false,
  );
  const [monthlyDeclinable, setMonthlyDeclinable] = useState(
    (plan as any).isMonthlyProtectionPlanDeclinable !== false,
  );

  useEffect(() => {
    setIsActive(!!plan.isActive);
    setLiabilityPercent(
      snapDeclinedLiabilityPercentToAllowedOption(
        resolveDeclinedProtectionOutOfPocketPercent({
          percent: (plan as any).declinedProtectionOutOfPocketPercent,
          legacyPolicy: (plan as any).declinedProtectionOutOfPocketAmt,
        }),
      ),
    );
    setPlanDetails(plan.policyDetails || '');
    setDescription(plan.description || '');
    setDailyDeclinable((plan as any).isDailyProtectionPlanDeclinable !== false);
    setMonthlyDeclinable((plan as any).isMonthlyProtectionPlanDeclinable !== false);
  }, [plan]);

  const [createProtectionPlan, { loading: isCreating }] = useMutation<
    ICreateProtectionPlanMutation,
    ICreateProtectionPlanMutationVariables
  >(CREATE_PROTECTION_PLAN, {
    refetchQueries: [{ query: GET_PROTECTION_PLANS }],
    awaitRefetchQueries: true,
    onCompleted: async () => {
      toast.success('Decline coverage saved');
      await refetch();
    },
    onError: () => {
      toast.error('Failed to save decline coverage');
    },
  });

  const [updateProtectionPlan, { loading: isUpdating }] = useMutation<
    IUpdateProtectionPlanMutation,
    IUpdateProtectionPlanMutationVariables
  >(UPDATE_PROTECTION_PLAN, {
    refetchQueries: [{ query: GET_PROTECTION_PLANS }],
    awaitRefetchQueries: true,
    onCompleted: async () => {
      toast.success('Decline coverage updated');
      await refetch();
    },
    onError: () => {
      toast.error('Failed to update decline coverage');
    },
  });

  const handleSave = () => {
    const input = {
      plan: ProtectionPlanEnum.Declined,
      dailyPrice: 0,
      outOfPocket: 0,
      description: description,
      isActive: isActive,
      policyDetails: planDetails,
      isDailyProtectionPlanDeclinable: dailyDeclinable,
      isMonthlyProtectionPlanDeclinable: monthlyDeclinable,
      declinedProtectionOutOfPocketPercent: liabilityPercent,
    };
    if (!plan.id) {
      createProtectionPlan({ variables: { input } });
    } else {
      updateProtectionPlan({
        variables: {
          input: {
            id: plan.id,
            ...input,
          },
        },
      });
    }
  };

  return (
    <DeclinedPlanContainer key="declined-plan">
      <StyledRadio
        value={plan}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          setSelected(plan);
        }}
        as="div"
      >
        <RadioContent as="div">
          <PlanInfo>
            <PlanName>Decline coverage</PlanName>
            <PlanDetails>
              <div>Maximum liability: {declinedOutOfPocketLabel(plan, liabilityPercent)}</div>
              <Separator aria-hidden="true" />
              <div>{plan.isActive ? 'Active' : 'Inactive'}</div>
            </PlanDetails>
          </PlanInfo>
          {plan.isActive ? (
            <FontAwesomeIcon icon={faCheckCircle} color="green" />
          ) : (
            <FontAwesomeIcon icon={faMinusCircle} color="red" />
          )}
        </RadioContent>
      </StyledRadio>
      {isSelected ? (
        <InputContainer>
          <DeSelectButton onClick={onDeselect}>
            <FontAwesomeIcon icon={faChevronUp} size="xl" color="darkgreen" />
          </DeSelectButton>
          <InputField inVertical={false}>
            <StyledLabel>Active</StyledLabel>
            <SwitchComponent
              isActive={!!isActive}
              onChange={() => setIsActive(!isActive)}
              color="green"
            />
          </InputField>
          <InputField inVertical={false}>
            <StyledLabel>Allow decline for daily rentals</StyledLabel>
            <SwitchComponent
              isActive={dailyDeclinable}
              onChange={() => setDailyDeclinable(!dailyDeclinable)}
              color="green"
            />
          </InputField>
          <InputField inVertical={false}>
            <StyledLabel>Allow decline for monthly rentals</StyledLabel>
            <SwitchComponent
              isActive={monthlyDeclinable}
              onChange={() => setMonthlyDeclinable(!monthlyDeclinable)}
              color="green"
            />
          </InputField>
          <DeclinedDefaultText>
            Decline coverage has no plan price. Set the maximum guest liability as a percentage of
            the vehicle&apos;s value.
          </DeclinedDefaultText>
          <InputField>
            <StyledLabel>Maximum liability (% of vehicle value)</StyledLabel>
            <StyledSelect
              value={liabilityPercent}
              onChange={e => setLiabilityPercent(Number(e.target.value))}
            >
              {DECLINED_LIABILITY_PERCENT_OPTIONS.map(p => (
                <option key={p} value={p}>
                  {p}%
                </option>
              ))}
            </StyledSelect>
          </InputField>
          <InputField>
            <StyledLabel>Description</StyledLabel>
            <StyledDescriptionArea
              value={description}
              onChange={e => {
                e.stopPropagation();
                setDescription(e.target.value);
              }}
            />
          </InputField>
          <InputField>
            <StyledLabel>Plan Details</StyledLabel>
            <StyledArea
              value={planDetails}
              onChange={e => {
                e.stopPropagation();
                setPlanDetails(e.target.value);
              }}
            />
          </InputField>
          <StyledButton onClick={handleSave} disabled={isCreating || isUpdating}>
            Save
          </StyledButton>
        </InputContainer>
      ) : null}
    </DeclinedPlanContainer>
  );
}
